import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Plus, 
  FileBarChart, 
  Trash2, 
  Edit2, 
  Copy, 
  Calendar, 
  Ship,
  LayoutDashboard,
  FolderOpen,
  Search,
  Clock,
  Grid3x3,
  List
} from 'lucide-react';
import { formatDate } from '../../lib/pcerUtils';

const REPORT_TYPES = [
  { 
    id: 'pcer', 
    label: 'PCER Report', 
    icon: FileBarChart,
    description: 'Port Call Execution Report with vessel performance analytics'
  },
  // Future report types can be added here
  // { id: 'voyage-analysis', label: 'Voyage Analysis', icon: Ship, description: 'Detailed voyage performance analysis' },
];

export default function ReportsManager({ onOpenReport }) {
  const [savedReports, setSavedReports] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [showNewReportMenu, setShowNewReportMenu] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  useEffect(() => {
    loadSavedReports();
  }, []);

  const loadSavedReports = () => {
    try {
      const saved = localStorage.getItem('savedReports');
      if (saved) {
        setSavedReports(JSON.parse(saved));
      }
    } catch (err) {
      console.error('Error loading saved reports:', err);
    }
  };

  const saveReports = (reports) => {
    try {
      localStorage.setItem('savedReports', JSON.stringify(reports));
      setSavedReports(reports);
    } catch (err) {
      console.error('Error saving reports:', err);
    }
  };

  const createNewReport = (reportType) => {
    const newReport = {
      id: `report-${Date.now()}`,
      type: reportType,
      name: `New ${REPORT_TYPES.find(t => t.id === reportType)?.label} - ${new Date().toLocaleDateString()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      config: {
        dateRange: { startDate: '', endDate: '' },
        selectedVesselTypes: [],
        selectedVessels: [],
        activeTab: 'table'
      },
      data: null,
      dashboard: {
        widgets: [],
        layouts: []
      }
    };

    const updated = [newReport, ...savedReports];
    saveReports(updated);
    setShowNewReportMenu(false);
    
    // Open the report immediately
    if (onOpenReport) {
      onOpenReport(newReport);
    }
  };

  const deleteReport = (reportId) => {
    if (confirm('Are you sure you want to delete this report?')) {
      const updated = savedReports.filter(r => r.id !== reportId);
      saveReports(updated);
    }
  };

  const duplicateReport = (report) => {
    const duplicate = {
      ...report,
      id: `report-${Date.now()}`,
      name: `${report.name} (Copy)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const updated = [duplicate, ...savedReports];
    saveReports(updated);
  };

  const startEditing = (report) => {
    setEditingId(report.id);
    setEditingName(report.name);
  };

  const saveEdit = (reportId) => {
    const updated = savedReports.map(r => 
      r.id === reportId 
        ? { ...r, name: editingName, updatedAt: new Date().toISOString() }
        : r
    );
    saveReports(updated);
    setEditingId(null);
    setEditingName('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const filteredReports = savedReports.filter(report => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      report.name.toLowerCase().includes(query) ||
      report.type.toLowerCase().includes(query)
    );
  });

  const getReportIcon = (type) => {
    const reportType = REPORT_TYPES.find(t => t.id === type);
    return reportType ? reportType.icon : FileBarChart;
  };

  return (
    <div className="h-full overflow-auto bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Reports Manager</h1>
          <p className="text-gray-600">Create, manage, and organize your saved reports</p>
        </div>

        {/* Action Bar */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search reports..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* View Toggle */}
          <div className="flex gap-1 border rounded-lg p-1">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="gap-2"
            >
              <Grid3x3 className="w-4 h-4" />
              Grid
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="gap-2"
            >
              <List className="w-4 h-4" />
              List
            </Button>
          </div>
          
          <div className="relative">
            <Button 
              onClick={() => setShowNewReportMenu(!showNewReportMenu)}
              size="lg"
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              New Report
            </Button>

            {showNewReportMenu && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowNewReportMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border z-20">
                  <div className="p-3 border-b">
                    <h3 className="font-semibold">Select Report Type</h3>
                  </div>
                  <div className="p-2">
                    {REPORT_TYPES.map(type => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.id}
                          onClick={() => createNewReport(type.id)}
                          className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-left"
                        >
                          <Icon className="w-5 h-5 mt-0.5 text-blue-600" />
                          <div>
                            <div className="font-medium">{type.label}</div>
                            <div className="text-sm text-gray-600">{type.description}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Reports</p>
                  <p className="text-3xl font-bold">{savedReports.length}</p>
                </div>
                <FolderOpen className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">PCER Reports</p>
                  <p className="text-3xl font-bold">
                    {savedReports.filter(r => r.type === 'pcer').length}
                  </p>
                </div>
                <FileBarChart className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Last Modified</p>
                  <p className="text-sm font-bold">
                    {savedReports.length > 0 
                      ? formatDate(savedReports[0].updatedAt)
                      : 'N/A'}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Reports View */}
        {filteredReports.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <FolderOpen className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-semibold mb-2">
                  {searchQuery ? 'No reports found' : 'No reports yet'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {searchQuery 
                    ? 'Try adjusting your search query'
                    : 'Create your first report to get started'}
                </p>
                {!searchQuery && (
                  <Button onClick={() => setShowNewReportMenu(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Report
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredReports.map(report => {
              const Icon = getReportIcon(report.type);
              const reportType = REPORT_TYPES.find(t => t.id === report.type);
              
              return (
                <Card 
                  key={report.id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer group"
                  onClick={() => onOpenReport && onOpenReport(report)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                          <Icon className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          {editingId === report.id ? (
                            <Input
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEdit(report.id);
                                if (e.key === 'Escape') cancelEdit();
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onBlur={() => saveEdit(report.id)}
                              autoFocus
                              className="h-7 text-sm"
                            />
                          ) : (
                            <CardTitle className="text-base truncate">
                              {report.name}
                            </CardTitle>
                          )}
                          <CardDescription className="text-xs mt-1">
                            {reportType?.label}
                          </CardDescription>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      {/* Date Range */}
                      {report.config?.dateRange?.startDate && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span className="text-xs">
                            {formatDate(report.config.dateRange.startDate)} - {formatDate(report.config.dateRange.endDate)}
                          </span>
                        </div>
                      )}
                      
                      {/* Vessels */}
                      {report.config?.selectedVessels?.length > 0 && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Ship className="w-4 h-4" />
                          <span className="text-xs">
                            {report.config.selectedVessels.length} vessel{report.config.selectedVessels.length > 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                      
                      {/* Dashboard */}
                      {report.dashboard?.widgets?.length > 0 && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <LayoutDashboard className="w-4 h-4" />
                          <span className="text-xs">
                            {report.dashboard.widgets.length} widget{report.dashboard.widgets.length > 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                      
                      {/* Updated Date */}
                      <div className="text-xs text-gray-500 pt-2 border-t">
                        Updated {formatDate(report.updatedAt)}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-1 mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditing(report);
                        }}
                        title="Rename"
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          duplicateReport(report);
                        }}
                        title="Duplicate"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteReport(report.id);
                        }}
                        className="ml-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          // List View
          <div className="space-y-3">
            {filteredReports.map(report => {
              const Icon = getReportIcon(report.type);
              const reportType = REPORT_TYPES.find(t => t.id === report.type);
              
              return (
                <Card 
                  key={report.id} 
                  className="hover:shadow-md transition-shadow cursor-pointer group"
                  onClick={() => onOpenReport && onOpenReport(report)}
                >
                  <CardContent className="py-4">
                    <div className="flex items-center gap-4">
                      {/* Icon */}
                      <div className="bg-blue-100 p-3 rounded-lg flex-shrink-0">
                        <Icon className="w-6 h-6 text-blue-600" />
                      </div>
                      
                      {/* Report Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            {editingId === report.id ? (
                              <Input
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveEdit(report.id);
                                  if (e.key === 'Escape') cancelEdit();
                                }}
                                onClick={(e) => e.stopPropagation()}
                                onBlur={() => saveEdit(report.id)}
                                autoFocus
                                className="h-8 font-semibold"
                              />
                            ) : (
                              <h3 className="text-lg font-semibold truncate">{report.name}</h3>
                            )}
                            <p className="text-sm text-gray-600 mt-1">{reportType?.label}</p>
                          </div>
                          
                          {/* Stats in List View */}
                          <div className="flex items-center gap-6 text-sm">
                            {report.config?.dateRange?.startDate && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <Calendar className="w-4 h-4" />
                                <span>
                                  {formatDate(report.config.dateRange.startDate)} - {formatDate(report.config.dateRange.endDate)}
                                </span>
                              </div>
                            )}
                            
                            {report.config?.selectedVessels?.length > 0 && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <Ship className="w-4 h-4" />
                                <span>
                                  {report.config.selectedVessels.length} vessel{report.config.selectedVessels.length > 1 ? 's' : ''}
                                </span>
                              </div>
                            )}
                            
                            {report.dashboard?.widgets?.length > 0 && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <LayoutDashboard className="w-4 h-4" />
                                <span>
                                  {report.dashboard.widgets.length} widget{report.dashboard.widgets.length > 1 ? 's' : ''}
                                </span>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2 text-gray-500">
                              <Clock className="w-4 h-4" />
                              <span className="text-xs">
                                {formatDate(report.updatedAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditing(report);
                          }}
                          title="Rename"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            duplicateReport(report);
                          }}
                          title="Duplicate"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteReport(report.id);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
